import Phaser from "phaser";

/**
 * Auras: efeitos de energia em volta do avatar, ganhos com easter eggs (o
 * servidor decide quem tem; ver server/internal/world/aura.go). Por enquanto
 * so existe a "saiyajin": chamas branco-azuladas subindo, brilho pulsando e um raio
 * de vez em quando.
 */

interface AuraStyle {
  /** cores das chamas, da mais clara para a mais quente */
  flames: number[];
  glow: number;
  bolt: number;
}

const STYLES: Record<string, AuraStyle> = {
  saiyajin: { flames: [0xffffff, 0xc4eaff, 0x86cfff, 0x48a8f5], glow: 0x7cc8ff, bolt: 0x2f8fff },
};

/** Altura aproximada do corpo, a partir do pe (o quadro tem 48px). */
const BODY_H = 36;
const BOLT_MIN_MS = 650;
const BOLT_MAX_MS = 1600;
const BOLT_MS = 90;

const FLAME_W = 5;
const FLAME_H = 8;
/** Gota de fogo; "o" e o miolo, mais claro. */
const FLAME_ROWS = ["..#..", ".###.", ".#o#.", "##o##", "#ooo#", "#ooo#", ".#o#.", "..#.."];

const flameKey = (id: string) => `aura-flame-${id}`;
const glowKey = (id: string) => `aura-glow-${id}`;

function css(color: number, alpha = 1): string {
  return `rgba(${(color >> 16) & 255},${(color >> 8) & 255},${color & 255},${alpha})`;
}

/**
 * Gera (uma vez por aura) as texturas ja coloridas: um quadro de chama por cor
 * e o brilho redondo. Sem tint, porque o renderizador Canvas (fallback do
 * Phaser.AUTO sem WebGL) ignora tint.
 */
function ensureTextures(scene: Phaser.Scene, id: string, style: AuraStyle): void {
  if (!scene.textures.exists(flameKey(id))) {
    const tex = scene.textures.createCanvas(flameKey(id), FLAME_W * style.flames.length, FLAME_H);
    if (tex) {
      const ctx = tex.getContext();
      style.flames.forEach((color, i) => {
        const core = style.flames[Math.max(0, i - 1)];
        FLAME_ROWS.forEach((row, y) =>
          [...row].forEach((ch, x) => {
            if (ch === ".") return;
            ctx.fillStyle = css(ch === "o" ? (i === 0 ? 0xffffff : core) : color);
            ctx.fillRect(i * FLAME_W + x, y, 1, 1);
          }),
        );
        tex.add(i, 0, i * FLAME_W, 0, FLAME_W, FLAME_H);
      });
      tex.refresh();
    }
  }
  if (!scene.textures.exists(glowKey(id))) {
    const size = 64;
    const tex = scene.textures.createCanvas(glowKey(id), size, size);
    if (tex) {
      const ctx = tex.getContext();
      const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
      grad.addColorStop(0, css(style.glow, 0.85));
      grad.addColorStop(0.5, css(style.glow, 0.4));
      grad.addColorStop(1, css(style.glow, 0));
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, size, size);
      tex.refresh();
    }
  }
}

/** Ponto aleatorio na "casca" do corpo: mais nas laterais, como uma labareda. */
function bodyEdge(maxY: number): Phaser.Types.GameObjects.Particles.RandomZoneSource {
  return {
    getRandomPoint(point: Phaser.Types.Math.Vector2Like) {
      const side = Math.random() < 0.5 ? -1 : 1;
      point.x = side * (4 + Math.random() * 8);
      point.y = -Math.random() * maxY;
      return point;
    },
  };
}

export class AuraEffect {
  readonly id: string;
  private readonly glow: Phaser.GameObjects.Image;
  private readonly back: Phaser.GameObjects.Particles.ParticleEmitter;
  private readonly front: Phaser.GameObjects.Particles.ParticleEmitter;
  private readonly bolt: Phaser.GameObjects.Graphics;
  private readonly style: AuraStyle;
  private nextBolt = 0;
  private boltUntil = 0;
  private x = 0;
  private y = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    id: string,
  ) {
    this.id = id;
    this.style = STYLES[id] ?? STYLES.saiyajin;
    ensureTextures(scene, id, this.style);
    const frames = this.style.flames.map((_, i) => i);
    const flames = {
      frame: frames,
      speedY: { min: -95, max: -40 },
      speedX: { min: -8, max: 8 },
      scale: { start: 1.3, end: 0.3 },
      lifespan: { min: 350, max: 650 },
    };

    // Mistura normal, nao aditiva: o chao da praca e claro e a cor clara somada
    // a ele vira branco e some.
    this.glow = scene.add.image(0, 0, glowKey(id)).setAlpha(0.5);
    // atras do corpo: a labareda cheia
    this.back = scene.add.particles(0, 0, flameKey(id), {
      ...flames,
      alpha: { start: 0.9, end: 0 },
      frequency: 10,
      emitZone: { type: "random", source: bodyEdge(BODY_H) },
    });
    // na frente: poucas e so da cintura para baixo, para nao tampar o rosto
    this.front = scene.add.particles(0, 0, flameKey(id), {
      ...flames,
      alpha: { start: 0.55, end: 0 },
      frequency: 45,
      emitZone: { type: "random", source: bodyEdge(BODY_H * 0.45) },
    });
    this.bolt = scene.add.graphics();
    this.nextBolt = scene.time.now + Phaser.Math.Between(BOLT_MIN_MS, BOLT_MAX_MS);
  }

  /** (x, y) e o pe do sprite (ja com o pulo); depth e a do sprite. */
  update(x: number, y: number, depth: number): void {
    this.x = x;
    this.y = y;
    const now = this.scene.time.now;
    const pulse = Math.sin(now / 140);
    this.glow
      .setPosition(x, y - BODY_H / 2)
      .setScale(0.62 + pulse * 0.04, 0.82 + pulse * 0.05)
      .setAlpha(0.6 + pulse * 0.12)
      .setDepth(depth - 0.3);
    this.back.setPosition(x, y).setDepth(depth - 0.2);
    this.front.setPosition(x, y).setDepth(depth + 0.2);

    this.bolt.setDepth(depth + 0.3);
    if (now >= this.nextBolt) {
      this.drawBolt();
      this.boltUntil = now + BOLT_MS;
      this.nextBolt = now + Phaser.Math.Between(BOLT_MIN_MS, BOLT_MAX_MS);
    } else if (now >= this.boltUntil) {
      this.bolt.clear();
    }
  }

  /** Raio em zigue-zague descendo pela lateral do corpo. */
  private drawBolt(): void {
    const side = Math.random() < 0.5 ? -1 : 1;
    let px = this.x + side * Phaser.Math.Between(11, 14);
    let py = this.y - BODY_H + Phaser.Math.Between(-4, 6);
    this.bolt.clear().lineStyle(1, this.style.bolt, 1).beginPath().moveTo(px, py);
    for (let i = 0; i < 4; i++) {
      px += side * Phaser.Math.Between(-2, 3);
      py += Phaser.Math.Between(4, 8);
      this.bolt.lineTo(px, py);
    }
    this.bolt.strokePath();
  }

  /** Explosao de energia quando a aura acende. */
  burst(): void {
    const { x, y } = this;
    const cy = y - BODY_H / 2;
    const depth = this.front.depth + 1;
    const ring = this.scene.add
      .image(x, cy, glowKey(this.id))
      .setDepth(depth)
      .setScale(0.3);
    this.scene.tweens.add({
      targets: ring,
      scale: 3,
      alpha: { from: 1, to: 0 },
      duration: 700,
      ease: "Cubic.Out",
      onComplete: () => ring.destroy(),
    });
    const sparks = this.scene.add.particles(x, cy, flameKey(this.id), {
      frame: this.style.flames.map((_, i) => i),
      speed: { min: 60, max: 160 },
      angle: { min: 0, max: 360 },
      scale: { start: 1.2, end: 0 },
      alpha: { start: 1, end: 0 },
      lifespan: { min: 400, max: 800 },
      emitting: false,
    });
    sparks.setDepth(depth);
    sparks.explode(36);
    this.scene.time.delayedCall(900, () => sparks.destroy());
  }

  destroy(): void {
    this.glow.destroy();
    this.back.destroy();
    this.front.destroy();
    this.bolt.destroy();
  }
}

const BAR_W = 28;
const BAR_H = 4;
/** Distancia do pe ate o topo da barrinha. */
const BAR_OFFSET = 4;

/**
 * Barrinha de carga embaixo do personagem (so o dono ve). O servidor manda a
 * fracao a cada snapshot; aqui ela so e suavizada.
 */
export class ChargeBar {
  private readonly g: Phaser.GameObjects.Graphics;
  private target = 0;
  private shown = 0;

  constructor(scene: Phaser.Scene) {
    this.g = scene.add.graphics().setVisible(false);
  }

  set(progress: number): void {
    if (progress > 0 && this.target === 0) this.shown = progress; // aparece ja na fracao certa
    this.target = progress;
  }

  update(x: number, y: number, depth: number, delta: number, now: number): void {
    if (this.target <= 0) {
      this.g.setVisible(false);
      this.shown = 0;
      return;
    }
    this.shown += (this.target - this.shown) * Math.min(1, (delta / 1000) * 8);
    const left = -BAR_W / 2;
    const fill = Math.round((BAR_W - 2) * Math.min(1, this.shown));
    // perto de encher, o dourado pisca para dar suspense
    const hot = this.shown > 0.85 ? 0.75 + 0.25 * Math.sin(now / 60) : 1;
    this.g.clear();
    this.g.fillStyle(0x24223a, 0.85);
    this.g.fillRoundedRect(left, 0, BAR_W, BAR_H, 2);
    if (fill > 0) {
      this.g.fillStyle(0xffc928, hot);
      this.g.fillRect(left + 1, 1, fill, BAR_H - 2);
      this.g.fillStyle(0xfff6b0, hot);
      this.g.fillRect(left + 1, 1, fill, 1);
    }
    this.g.setPosition(Math.round(x), Math.round(y) + BAR_OFFSET).setDepth(depth).setVisible(true);
  }

  destroy(): void {
    this.g.destroy();
  }
}
